import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./MortbuzzHomeLoan.css";
import "animate.css/animate.min.css";
import Seo from "../components/Seo";

const loanUses = [
  {
    icon: "bi bi-house-door",
    title: "New Home Purchase",
    text: "Finance the purchase of an eligible ready-to-move, resale or under-construction residential property.",
  },
  {
    icon: "bi bi-bricks",
    title: "Home Construction",
    text: "Get financial assistance to construct a residential property on an eligible plot.",
  },
  {
    icon: "bi bi-tools",
    title: "Renovation or Extension",
    text: "Renovate, repair or extend your existing residential property with suitable funding.",
  },
  {
    icon: "bi bi-arrow-repeat",
    title: "Balance Transfer",
    text: "Transfer your existing home loan to another lender for potentially better terms.",
  },
  {
    icon: "bi bi-plus-circle",
    title: "Top-Up Loan",
    text: "Access additional funds over an eligible existing home loan, subject to lender approval.",
  },
  {
    icon: "bi bi-buildings",
    title: "Property Investment",
    text: "Explore eligible home-loan options for residential property investment requirements.",
  },
];

const eligibilityItems = [
  {
    image: "/images/Bank Pages/salaried-image-icon.png",
    alt: "Applicant employment profile",
    title: "Employment Profile",
    text: "Salaried and self-employed applicants with a stable income may apply.",
  },
  {
    image: "/images/Bank Pages/age.png",
    alt: "Applicant age eligibility",
    title: "Applicant Age",
    text: "Age eligibility generally depends on lender policy and the selected loan tenure.",
  },
  {
    image: "/images/Bank Pages/3-month-experienced.png",
    alt: "Work experience eligibility",
    title: "Work Experience",
    text: "Stable employment or an established business profile may be required.",
  },
  {
    image: "/images/Bank Pages/minimum-income_20000.png",
    alt: "Monthly income eligibility",
    title: "Monthly Income",
    text: "Income eligibility depends on loan amount, obligations and repayment capacity.",
  },
];

const documentGroups = [
  {
    iconImage: "/images/Bank Pages/photo-identy.png",
    title: "Identity Documents",
    items: [
      "PAN Card",
      "Aadhaar Card",
      "Passport",
      "Voter ID",
      "Driving Licence",
      "Recent passport-size photographs",
    ],
  },
  {
    iconImage: "/images/Bank Pages/proof-of-residence.png",
    title: "Address Documents",
    items: [
      "Aadhaar Card",
      "Passport",
      "Driving Licence",
      "Registered rent agreement",
      "Recent utility bill",
      "Bank statement showing current address",
    ],
  },
  {
    iconImage: "/images/Bank Pages/income-proof.png",
    title: "Income Documents",
    items: [
      "Recent salary slips",
      "Salary account bank statements",
      "Form 16",
      "Income tax returns",
      "Audited financial statements, where applicable",
      "Business registration or professional proof",
    ],
  },
  {
    iconImage: "/images/Bank Pages/other-document.png",
    title: "Property Documents",
    items: [
      "Agreement for sale",
      "Allotment letter",
      "Property tax receipt",
      "Approved building plan",
      "Possession or occupancy certificate",
      "Title and ownership documents",
      "Construction estimate, where applicable",
    ],
  },
];

const applicationSteps = [
  {
    title: "Share Your Requirement",
    text: "Tell us your preferred loan amount, property details, income profile and current financial obligations.",
  },
  {
    title: "Eligibility Review",
    text: "Our team reviews your basic eligibility and identifies potentially suitable lending options.",
  },
  {
    title: "Compare Available Options",
    text: "Compare indicative rates, tenure, charges and lender requirements before selecting a bank.",
  },
  {
    title: "Document Submission",
    text: "Submit the required financial, identity, address and property documents with our assistance.",
  },
  {
    title: "Bank Verification",
    text: "The lender conducts credit assessment, income verification, legal checks and property valuation.",
  },
  {
    title: "Sanction and Disbursement",
    text: "After approval and completion of lender formalities, the sanctioned amount is disbursed.",
  },
];

const bankRates = [
  {
    bank: "Bank of India",
    link: "https://bankofindia.co.in/home-loan",
    rate: "7.10%*",
    amount: "₹10 lakh–₹10 crore",
    tenure: "Up to 30 years",
    fee: "As per bank policy",
  },
  {
    bank: "Bank of Maharashtra",
    link: "https://bankofmaharashtra.in/personal-banking/loans/home-loan",
    rate: "7.10%*",
    amount: "₹10 lakh–₹10 crore",
    tenure: "Up to 30 years",
    fee: "Selected offers may apply",
  },
  {
    bank: "Central Bank of India",
    link: "https://www.centralbankofindia.co.in/en/housing-loan",
    rate: "7.10%*",
    amount: "₹10 lakh–₹10 crore",
    tenure: "Up to 30 years",
    fee: "As per bank policy",
  },
  {
    bank: "Indian Overseas Bank",
    link: "https://www.iob.in/home-loans.aspx",
    rate: "7.10%*",
    amount: "₹10 lakh–₹10 crore",
    tenure: "Up to 30 years",
    fee: "As per bank policy",
  },
  {
    bank: "HDFC Bank",
    link: "https://www.hdfc.com/",
    rate: "7.20%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "Up to 30 years",
    fee: "As per bank policy",
  },
  {
    bank: "Bank of Baroda",
    link: "https://www.bankofbaroda.in/personal-banking/loans/home-loan",
    rate: "7.20%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "Up to 30 years",
    fee: "Selected offers may apply",
  },
  {
    bank: "ICICI Bank",
    link: "https://www.icicibank.com/personal-banking/loans/home-loan",
    rate: "7.30%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "Up to 30 years",
    fee: "As per bank policy",
  },
  {
    bank: "IDBI Bank",
    link: "https://www.idbibank.in/home-loan.aspx",
    rate: "7.35%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "Up to 30 years",
    fee: "As per bank policy",
  },
  {
    bank: "Axis Bank",
    link: "https://www.axisbank.com/retail/loans/home-loan",
    rate: "7.50%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "Up to 30 years",
    fee: "As per bank policy",
  },
  {
    bank: "Kotak Mahindra Bank",
    link: "https://www.kotak.com/en/personal-banking/loans/home-loan.html",
    rate: "7.99%*",
    amount: "₹10 lakh–₹10 crore",
    tenure: "Up to 30 years",
    fee: "As per bank policy",
  },
  {
    bank: "Standard Chartered Bank",
    link: "https://www.sc.com/in/loans/home-property-loans/home-loans/",
    rate: "8.95%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "Up to 30 years",
    fee: "As per bank policy",
  },
];

const faqItems = [
  {
    question: "Who can apply for a home loan through MONAYRA?",
    answer:
      "Salaried and self-employed applicants with a stable income and satisfactory credit profile may apply. Final eligibility depends on lender policy, income, age, obligations, credit score and property details.",
  },
  {
    question: "How much home loan can I receive?",
    answer:
      "The eligible amount depends on income, repayment capacity, age, existing EMIs, credit score, property value and the lender’s loan-to-value policy.",
  },
  {
    question: "Which documents are generally required?",
    answer:
      "Common documents include PAN, Aadhaar, income proof, bank statements, employment or business proof and property documents. The final list varies by applicant and lender.",
  },
  {
    question: "How long does home-loan approval take?",
    answer:
      "Processing time depends on document completeness, credit assessment, legal verification and property valuation. MONAYRA follows up with the lender throughout the process.",
  },
  {
    question: "What interest rate will I receive?",
    answer:
      "The final rate depends on the selected lender, applicant profile, credit score, income, loan amount and prevailing lender policies. Displayed rates are indicative.",
  },
  {
    question: "Is doorstep document assistance available?",
    answer:
      "Doorstep document assistance may be available in selected service areas, subject to operational availability.",
  },
  {
    question: "What processing fees apply?",
    answer:
      "Processing fees and other charges vary by lender, product and offer. Charges should be confirmed in the lender’s final sanction and fee schedule.",
  },
  {
    question: "Can I finance an under-construction property?",
    answer:
      "Eligible under-construction, ready-to-move and resale residential properties may be financed, subject to lender approval and required legal and technical checks.",
  },
  {
    question: "Can I transfer my existing home loan?",
    answer:
      "Yes. MONAYRA can assist with comparing balance-transfer options that may provide a lower interest rate, reduced EMI or a top-up facility, subject to eligibility.",
  },
  {
    question: "Can I prepay or foreclose my home loan?",
    answer:
      "Prepayment and foreclosure rules depend on the loan type and lender policy. Review the sanction terms and applicable regulatory requirements before proceeding.",
  },
];

function MortbuzzHomeLoan() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      <Seo
        title="Home Loan in Pune | MONAYRA (OPC) PRIVATE LIMITED"
        description="Explore home loan options in Pune with MONAYRA (OPC) PRIVATE LIMITED. Get assistance with lender comparison, eligibility, documentation and loan processing."
        keywords="home loan in Pune, home loan assistance, home loan eligibility, home loan balance transfer, apply home loan online"
        canonical="https://www.mortbuzz.in/mortbuzz-home-loan"
      />

      <main className="monayra-home-loan-page">
        <section className="home-loan-hero">
          <div className="home-loan-hero-overlay"></div>

          <div className="home-loan-hero-container">
            <span className="home-loan-hero-eyebrow animate__animated animate__fadeInDown">
              Home Finance Solutions
            </span>

            <h1 className="animate__animated animate__fadeInUp">
              Build your dream home with the right
              <span> home-loan assistance</span>
            </h1>

            <p className="animate__animated animate__fadeInUp animate__delay-1s">
              Compare suitable lending options, understand your eligibility
              and receive professional support from application to
              disbursement.
            </p>

            <div className="home-loan-hero-actions animate__animated animate__fadeInUp animate__delay-1s">
              <Link to="/apply-now" className="home-loan-primary-btn">
                Apply for Home Loan
                <i className="bi bi-arrow-right"></i>
              </Link>

              <a href="#home-loan-rates" className="home-loan-secondary-btn">
                <i className="bi bi-bar-chart"></i>
                View Indicative Rates
              </a>
            </div>

            <div className="home-loan-hero-highlights">
              <div>
                <i className="bi bi-bank"></i>
                Multiple lenders
              </div>

              <div>
                <i className="bi bi-file-earmark-check"></i>
                Documentation support
              </div>

              <div>
                <i className="bi bi-house-check"></i>
                Doorstep assistance
              </div>
            </div>
          </div>
        </section>

        <section className="home-loan-intro-section">
          <div className="home-loan-page-container home-loan-intro-grid">
            <div className="home-loan-intro-image">
              <img
                src="/images/FrontPage/home-loan-mortbuzz.jpg"
                alt="Residential home loan assistance"
              />

              <div className="home-loan-intro-image-overlay"></div>

              <div className="home-loan-rate-badge">
                <span>Indicative starting rate</span>
                <strong>7.10%*</strong>
                <small>Subject to lender terms</small>
              </div>
            </div>

            <div className="home-loan-intro-content">
              <span className="home-loan-section-eyebrow">
                Why Choose MONAYRA?
              </span>

              <h2>
                Professional support for your
                <span> complete home-loan journey</span>
              </h2>

              <p>
                MONAYRA (OPC) PRIVATE LIMITED assists home-loan applicants
                with lender comparison, eligibility review, documentation,
                application submission and regular processing updates.
              </p>

              <div className="home-loan-benefit-list">
                {[
                  "Indicative rates starting from 7.10%*",
                  "Options from selected public and private banks",
                  "Assistance for salaried and self-employed applicants",
                  "Balance transfer and top-up support",
                  "Doorstep document assistance in selected locations",
                  "Support from application to disbursement",
                ].map((item) => (
                  <div key={item}>
                    <span>
                      <i className="bi bi-check-lg"></i>
                    </span>
                    {item}
                  </div>
                ))}
              </div>

              <Link to="/apply-now" className="home-loan-inline-cta">
                Check Your Eligibility
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="home-loan-uses-section">
          <div className="home-loan-page-container">
            <div className="home-loan-section-header">
              <span className="home-loan-section-eyebrow">
                Loan Purposes
              </span>

              <h2>
                Common uses of a
                <span> home loan</span>
              </h2>

              <p>
                Home-loan products may support property purchase,
                construction, renovation, balance transfer and other eligible
                residential requirements.
              </p>
            </div>

            <div className="home-loan-uses-grid">
              {loanUses.map((item) => (
                <article className="home-loan-use-card" key={item.title}>
                  <span className="home-loan-use-icon">
                    <i className={item.icon}></i>
                  </span>

                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="home-loan-eligibility-section">
          <div className="home-loan-page-container">
            <div className="home-loan-section-header">
              <span className="home-loan-section-eyebrow">
                Basic Eligibility
              </span>

              <h2>
                Home-loan
                <span> eligibility factors</span>
              </h2>

              <p>
                Lenders assess several financial and personal factors before
                approving a home-loan application.
              </p>
            </div>

            <div className="home-loan-eligibility-grid">
              {eligibilityItems.map((item) => (
                <article
                  className="home-loan-eligibility-card"
                  key={item.title}
                >
                  <div className="home-loan-eligibility-image">
                    <img src={item.image} alt={item.alt} loading="lazy" />
                  </div>

                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>

            <div className="home-loan-eligibility-note">
              <i className="bi bi-info-circle"></i>
              Final eligibility depends on the lender’s policy and complete
              assessment of the applicant.
            </div>
          </div>
        </section>

        <section className="home-loan-cta-section">
          <div className="home-loan-page-container">
            <div className="home-loan-cta-card">
              <div>
                <span>Begin Your Application</span>
                <h2>Ready to explore your home-loan options?</h2>
                <p>
                  Submit your requirement and our team will help you understand
                  suitable lender options and documentation.
                </p>
              </div>

              <Link to="/apply-now" className="home-loan-primary-btn">
                Apply Now
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="home-loan-documents-section">
          <div className="home-loan-page-container">
            <div className="home-loan-section-header">
              <span className="home-loan-section-eyebrow">
                Documents Checklist
              </span>

              <h2>
                Documents generally required for a
                <span> home loan</span>
              </h2>

              <p>
                The final document list may differ according to your
                employment type, property and selected lender.
              </p>
            </div>

            <div className="home-loan-documents-grid">
              {documentGroups.map((group) => (
                <article
                  className="home-loan-document-card"
                  key={group.title}
                >
                  <div className="home-loan-document-heading">
                    <span>
                      <img
                        src={group.iconImage}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                      />
                    </span>

                    <h3>{group.title}</h3>
                  </div>

                  <ul>
                    {group.items.map((item) => (
                      <li key={item}>
                        <i className="bi bi-check-circle"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="home-loan-process-section">
          <div className="home-loan-page-container">
            <div className="home-loan-section-header">
              <span className="home-loan-section-eyebrow">
                Application Journey
              </span>

              <h2>
                A simple step-by-step
                <span> home-loan process</span>
              </h2>
            </div>

            <div className="home-loan-process-list">
              {applicationSteps.map((step, index) => (
                <article
                  className="home-loan-process-item"
                  key={step.title}
                >
                  <span className="home-loan-process-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="home-loan-rates"
          className="home-loan-rates-section"
        >
          <div className="home-loan-page-container">
            <div className="home-loan-section-header">
              <span className="home-loan-section-eyebrow">
                Indicative Bank Comparison
              </span>

              <h2>
                Home-loan
                <span> interest-rate overview</span>
              </h2>

              <p>
                Rates and charges shown below are indicative and should be
                confirmed directly with the selected lender.
              </p>
            </div>

            <div className="home-loan-table-wrapper">
              <table className="home-loan-rate-table">
                <thead>
                  <tr>
                    <th>Bank</th>
                    <th>Starting Rate</th>
                    <th>Indicative Amount</th>
                    <th>Maximum Tenure</th>
                    <th>Processing Fee</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {bankRates.map((bank) => (
                    <tr key={bank.bank}>
                      <td>
                        <a
                          href={bank.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {bank.bank}
                          <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                      </td>

                      <td>
                        <strong>{bank.rate}</strong>
                      </td>

                      <td>{bank.amount}</td>
                      <td>{bank.tenure}</td>
                      <td>{bank.fee}</td>

                      <td>
                        <Link
                          to="/apply-now"
                          className="home-loan-table-btn"
                        >
                          Apply
                          <i className="bi bi-arrow-right"></i>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="home-loan-table-note">
              <i className="bi bi-info-circle"></i>
              Rates, fees and limits can change. Final terms are provided by
              the lender after assessment.
            </div>
          </div>
        </section>

        <section className="home-loan-fee-section">
          <div className="home-loan-page-container home-loan-fee-grid">
            <div className="home-loan-fee-image">
              <img
                src="/images/FrontPage/loan-dealing.jpg"
                alt="Home-loan processing and documentation"
                loading="lazy"
              />
            </div>

            <div className="home-loan-fee-content">
              <span className="home-loan-section-eyebrow">
                Loan Charges
              </span>

              <h2>
                Understand the
                <span> processing fee</span>
              </h2>

              <p>
                Lenders may charge a processing fee to review documents,
                assess eligibility and process the home-loan application.
              </p>

              <p>
                The exact amount can differ according to lender policy, loan
                amount, applicant profile and available offer. Applicable
                taxes may also be charged.
              </p>

              <div className="home-loan-fee-highlight">
                <i className="bi bi-receipt"></i>

                <div>
                  <strong>Always confirm the complete fee schedule</strong>
                  <span>
                    Review processing, legal, valuation, documentation and
                    other applicable lender charges.
                  </span>
                </div>
              </div>

              <Link
                to="/mortbuzz-home-loan-offer"
                className="home-loan-inline-cta"
              >
                Explore Home-Loan Offers
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="home-loan-transfer-section">
          <div className="home-loan-page-container home-loan-transfer-grid">
            <div className="home-loan-transfer-content">
              <span className="home-loan-section-eyebrow">
                Balance Transfer
              </span>

              <h2>
                Reduce your burden with a
                <span> home-loan balance transfer</span>
              </h2>

              <p>
                A balance transfer allows an eligible borrower to move an
                outstanding home loan from the current lender to another
                lender offering potentially better terms.
              </p>

              <div className="home-loan-benefit-list">
                {[
                  "Potentially lower interest rate",
                  "Possible reduction in monthly EMI",
                  "Opportunity to revise repayment tenure",
                  "Top-up facility subject to eligibility",
                  "Documentation and lender coordination support",
                  "Assistance throughout transfer processing",
                ].map((item) => (
                  <div key={item}>
                    <span>
                      <i className="bi bi-check-lg"></i>
                    </span>
                    {item}
                  </div>
                ))}
              </div>

              <Link to="/apply-now" className="home-loan-inline-cta">
                Check Balance Transfer Options
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>

            <div className="home-loan-transfer-image">
              <img
                src="/images/FrontPage/Balance Transfer.jpg"
                alt="Home-loan balance transfer assistance"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <section className="home-loan-faq-section">
          <div className="home-loan-page-container">
            <div className="home-loan-section-header">
              <span className="home-loan-section-eyebrow">
                Common Questions
              </span>

              <h2>
                Home-loan
                <span> frequently asked questions</span>
              </h2>
            </div>

            <div className="home-loan-faq-layout">
              <div className="home-loan-faq-list">
                {faqItems.map((faq, index) => {
                  const isOpen = openFaq === index;

                  return (
                    <article
                      className={`home-loan-faq-item ${
                        isOpen ? "open" : ""
                      }`}
                      key={faq.question}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenFaq(isOpen ? null : index)
                        }
                        aria-expanded={isOpen}
                      >
                        <span className="home-loan-faq-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span>{faq.question}</span>

                        <i
                          className={
                            isOpen
                              ? "bi bi-dash-lg"
                              : "bi bi-plus-lg"
                          }
                        ></i>
                      </button>

                      {isOpen && (
                        <div className="home-loan-faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              <aside className="home-loan-help-card">
                <span className="home-loan-help-icon">
                  <i className="bi bi-headset"></i>
                </span>

                <span className="home-loan-help-eyebrow">
                  Need Assistance?
                </span>

                <h3>Speak with our home-loan team</h3>

                <p>
                  Share your income profile, property details and required
                  loan amount to receive guidance on suitable next steps.
                </p>

                <Link to="/contact" className="home-loan-primary-btn">
                  Contact Our Team
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <Link to="/apply-now" className="home-loan-help-secondary">
                  <i className="bi bi-file-earmark-text"></i>
                  Apply Online
                </Link>
              </aside>
            </div>
          </div>
        </section>

        <section className="home-loan-final-disclaimer">
          <div className="home-loan-page-container">
            <i className="bi bi-shield-exclamation"></i>

            <p>
              *Displayed rates and product details are indicative. Final
              approval, interest rate, loan amount, tenure, fees and other
              terms are determined by the selected lender based on its
              policies and the applicant’s complete profile. MONAYRA (OPC)
              PRIVATE LIMITED provides loan assistance and does not guarantee
              approval.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}

export default MortbuzzHomeLoan;